import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Check, X, ZoomIn, Image as ImageIcon, UserCircle2, ShieldCheck, Sparkles } from "lucide-react";
import BossYouTubePlayer from "@/components/BossYouTubePlayer";
import AdminPushNotifier from "@/components/AdminPushNotifier";
import TelegramBotManager from "@/components/TelegramBotManager";

const BOSS_EMAIL = "orioa98@gmail.com";

function timeAgo(iso) {
  if (!iso) return "";
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return `hace ${Math.floor(diff)}s`;
  if (diff < 3600) return `hace ${Math.floor(diff / 60)}m`;
  if (diff < 86400) return `hace ${Math.floor(diff / 3600)}h`;
  return `hace ${Math.floor(diff / 86400)}d`;
}

export default function Admin() {
  const { user, loaded } = useAuth();
  const [profiles, setProfiles] = useState([]);
  const [pendingProfiles, setPendingProfiles] = useState([]);
  const [stats, setStats] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [verifs, setVerifs] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [reports, setReports] = useState([]);
  const [tab, setTab] = useState(() => {
    try {
      const t = new URLSearchParams(window.location.search).get("tab");
      return ["pending", "profiles", "photos", "reviews", "verifs", "reports"].includes(t) ? t : "pending";
    } catch {
      return "pending";
    }
  });
  const [selectedPhotos, setSelectedPhotos] = useState(() => new Set());
  const [busyPhotoIds, setBusyPhotoIds] = useState(() => new Set());
  const [lightbox, setLightbox] = useState(null);
  const [photoFilter, setPhotoFilter] = useState("all"); // all | registration | dashboard
  const [bulkBusy, setBulkBusy] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [editingProfile, setEditingProfile] = useState(null);

  const load = async () => {
    const [p, pp, s, r, v, ph, rp] = await Promise.all([
      api.get("/admin/profiles"),
      api.get("/admin/profiles/pending"),
      api.get("/admin/stats"),
      api.get("/admin/reviews"),
      api.get("/admin/verifications"),
      api.get("/admin/photos/pending"),
      api.get("/admin/reports"),
    ]);
    setProfiles(p.data || []); setPendingProfiles(pp.data || []); setStats(s.data);
    setReviews(r.data || []); setVerifs(v.data || []);
    setPhotos(ph.data || []); setReports(rp.data || []);
  };

  useEffect(() => { if (loaded && user && (user.role === "admin" || user.role === "moderator")) load(); }, [loaded, user]);

  const setStatus = async (id, new_status) => {
    await api.post(`/admin/profiles/${id}/status?new_status=${new_status}`);
    load();
  };
  const deleteProfile = async (id, name) => {
    if (!window.confirm(`¿ELIMINAR PERMANENTEMENTE el perfil de ${name}?\n\nEsto borrará el perfil, sus fotos pendientes, reseñas, reportes y verificaciones. NO se puede deshacer.`)) return;
    try {
      await api.delete(`/admin/profiles/${id}`);
      load();
    } catch (e) {
      alert("Error: " + (e.response?.data?.detail || e.message));
    }
  };
  const moderateProfile = async (id, action) => {
    await api.post(`/admin/profiles/${id}/moderate?action=${action}`);
    // Optimistic removal from pending queue
    setPendingProfiles((prev) => prev.filter((p) => p.id !== id));
    // Refresh stats
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
  };
  const moderateReview = async (id, action) => {
    await api.post(`/admin/reviews/${id}/moderate?action=${action}`);
    load();
  };
  const moderateVerif = async (id, action) => {
    await api.post(`/admin/verifications/${id}/moderate?action=${action}`);
    load();
  };
  const moderatePhoto = async (id, action) => {
    setBusyPhotoIds((prev) => new Set(prev).add(id));
    try {
      await api.post(`/admin/photos/${id}/moderate?action=${action}`);
      // Optimistic: remove from local list, keep selection in sync
      setPhotos((prev) => prev.filter((p) => p.id !== id));
      setSelectedPhotos((prev) => {
        const n = new Set(prev); n.delete(id); return n;
      });
    } finally {
      setBusyPhotoIds((prev) => { const n = new Set(prev); n.delete(id); return n; });
    }
  };

  const bulkModeratePhotos = async (action) => {
    const ids = Array.from(selectedPhotos);
    if (!ids.length) return;
    if (!window.confirm(`${action === "approve" ? "Aprobar" : "Rechazar"} ${ids.length} foto${ids.length === 1 ? "" : "s"}?`)) return;
    setBulkBusy(true);
    try {
      await api.post(`/admin/photos/bulk-moderate`, { ids, action });
      setPhotos((prev) => prev.filter((p) => !selectedPhotos.has(p.id)));
      setSelectedPhotos(new Set());
    } finally {
      setBulkBusy(false);
    }
  };

  const togglePhoto = (id) => {
    setSelectedPhotos((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };
  const resolveReport = async (id, action) => {
    await api.post(`/admin/reports/${id}/resolve?action=${action}`);
    load();
  };

  if (loaded && (!user || (user.role !== "admin" && user.role !== "moderator"))) return <div className="p-12" data-testid="admin-denied">Acceso solo para administradores. <Link to="/login" className="underline" style={{ color: "var(--brand-primary)" }}>Acceder</Link></div>;
  if (!stats) return <div className="p-12" style={{ color: "var(--text-secondary)" }}>Cargando…</div>;

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-12 pt-16 pb-24" data-testid="admin-page">
      <div className="overline mb-3">Admin</div>
      <div className="flex items-center justify-between gap-4 mb-10 flex-wrap">
        <h1 className="font-serif text-5xl font-light" style={{ color: "var(--text-primary)" }}>Panel de <em style={{ color: "var(--brand-primary)", fontStyle: "italic" }}>administración</em></h1>
        <div className="flex gap-2 flex-wrap items-center">
          {(user?.role === "admin" || user?.role === "moderator") && <AdminPushNotifier />}
          {(user?.role === "admin" || user?.role === "moderator") && (
            <button
              type="button"
              className="btn-primary text-xs inline-flex items-center gap-2"
              onClick={() => setShowCreate(true)}
              data-testid="admin-create-profile-btn"
            >
              <UserCircle2 size={14} /> Crear perfil
            </button>
          )}
          {user?.role === "admin" && (
            <button
              type="button"
              className="btn-secondary text-xs inline-flex items-center gap-2"
              style={{ borderColor: "rgb(34,197,94)", color: "rgb(34,197,94)" }}
              onClick={async () => {
                if (!window.confirm(
                  "¿Rescatar perfiles huérfanos?\n\nSe creará un perfil (status pending) para cada Musa/Compañera que se registró pero no tiene perfil. Las fotos pendientes se vincularán automáticamente.\n\nEs seguro: si una musa ya tiene perfil, se ignora."
                )) return;
                try {
                  const { data } = await api.post("/admin/rescue-orphan-companions");
                  alert(`✅ Rescate completado\n\nPerfiles creados: ${data.rescued_count}\nYa tenían perfil (omitidas): ${data.already_had_profile}\n\nAhora ve a la pestaña "Pendientes" para revisar y aprobar.`);
                  load();
                } catch (e) {
                  alert("Error: " + (e.response?.data?.detail || e.message));
                }
              }}
              data-testid="admin-rescue-orphans-btn"
            >
              <UserCircle2 size={14} /> Rescatar huérfanos
            </button>
          )}
          {user?.role === "admin" && (
            <button
              type="button"
              className="btn-secondary text-xs inline-flex items-center gap-2"
              style={{ borderColor: "#3b82f6", color: "#3b82f6" }}
              onClick={async () => {
                if (!window.confirm("¿Asignar IDs cortos (5 caracteres) a todos los perfiles que no los tengan?\n\nEs seguro: idempotente, no toca los que ya tienen.")) return;
                try {
                  const { data } = await api.post("/admin/backfill-display-ids");
                  alert(`✅ Listo · IDs asignados: ${data.assigned_count}`);
                  load();
                } catch (e) {
                  alert("Error: " + (e.response?.data?.detail || e.message));
                }
              }}
              data-testid="admin-backfill-ids-btn"
            >
              <UserCircle2 size={14} /> IDs cortos
            </button>
          )}
          {user?.role === "admin" && (
            <button
              type="button"
              className="btn-secondary text-xs inline-flex items-center gap-2"
              style={{ borderColor: "var(--vip-gold)", color: "var(--vip-gold)" }}
              onClick={async () => {
                if (!window.confirm(
                  "¿Cargar los 18 perfiles demo? Se insertan los que aún no existan. Las cuentas ya registradas no se tocan."
                )) return;
                try {
                  const { data } = await api.post("/admin/seed-demo-profiles");
                  alert(`✓ Listo\n\nCreados: ${data.created}\nOmitidos (ya existían): ${data.skipped_existing}\nTotal aprobados ahora: ${data.total_approved_profiles}`);
                  load();
                } catch (e) {
                  alert("Error: " + (e.response?.data?.detail || e.message));
                }
              }}
              data-testid="admin-seed-btn"
            >
              <Sparkles size={14} /> Cargar 18 demo
            </button>
          )}
        </div>
      </div>

      <TelegramBotManager />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12" data-testid="admin-stats">
        {[
          { label: "Usuarios", value: stats.users },
          { label: "Perfiles", value: stats.profiles },
          { label: "Perfiles pendientes", value: stats.pending_profiles ?? 0 },
          { label: "VIP activos", value: stats.vip },
          { label: "Verificados", value: stats.verified },
          { label: "Pagos realizados", value: stats.paid_transactions },
          { label: "Reseñas pendientes", value: stats.pending_reviews },
          { label: "Verificaciones pendientes", value: stats.pending_verifications },
        ].map((s) => (
          <div key={s.label} className="p-5" style={{ background: "var(--bg-surface)", border: "1px solid var(--border-subtle)" }} data-testid={`stat-${s.label}`}>
            <div className="overline mb-2">{s.label}</div>
            <div className="font-serif text-3xl" style={{ color: "var(--brand-primary)" }}>{s.value}</div>
          </div>
        ))}
      </div>

      <div className="flex gap-2 mb-8 overflow-x-auto" style={{ borderBottom: "1px solid var(--border-subtle)" }} data-testid="admin-tabs">
        {[
          { id: "pending", label: `Perfiles pendientes (${pendingProfiles.length})` },
          { id: "profiles", label: `Perfiles (${profiles.length})` },
          { id: "photos", label: `Fotos pendientes (${photos.length})` },
          { id: "reviews", label: `Reseñas pendientes (${reviews.length})` },
          { id: "verifs", label: `Verificaciones pendientes (${verifs.length})` },
          { id: "reports", label: `Reportes (${reports.length})` },
        ].map((t) => (
          <button key={t.id} onClick={() => setTab(t.id)} className="px-5 py-3 text-xs uppercase tracking-[0.2em] whitespace-nowrap"
            style={{ color: tab===t.id ? "var(--brand-primary)" : "var(--text-secondary)", borderBottom: `2px solid ${tab===t.id ? "var(--brand-primary)" : "transparent"}` }}
            data-testid={`admin-tab-${t.id}`}>{t.label}</button>
        ))}
      </div>

      {tab === "pending" && (
        <div data-testid="admin-pending-profiles">
          {pendingProfiles.length === 0 ? (
            <div className="text-center py-16" style={{ background: "var(--bg-surface)", border: "1px dashed var(--border-accent)" }} data-testid="admin-pending-empty">
              <ShieldCheck size={40} strokeWidth={1} color="var(--vip-gold)" className="mx-auto mb-4" />
              <p className="font-serif text-2xl mb-2" style={{ color: "var(--text-primary)" }}>Sin perfiles pendientes</p>
              <p className="text-sm" style={{ color: "var(--text-muted)" }}>Los nuevos registros aparecerán aquí.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {pendingProfiles.map((p) => {
                const cover = p.hero_photo || (p.photos && p.photos[0]) || "";
                return (
                  <div key={p.id} className="p-4 flex flex-col gap-3" style={{ background: "var(--bg-surface)", border: "1px solid var(--brand-primary)" }} data-testid={`pending-card-${p.id}`}>
                    <div className="flex gap-4">
                      <div style={{ width: 100, height: 130, background: "var(--bg-elevated)", flexShrink: 0 }}>
                        {cover ? (
                          <img src={cover} alt={p.stage_name} className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center" style={{ color: "var(--text-muted)", fontSize: 10 }}>
                            Sin foto
                          </div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-serif text-xl" style={{ color: "var(--text-primary)" }} data-testid={`pending-name-${p.id}`}>
                          {p.stage_name}
                        </h3>
                        <p className="text-xs mt-1" style={{ color: "var(--text-secondary)" }}>
                          {p.city}{p.state && `, ${p.state}`} · {p.age} años
                        </p>
                        <p className="text-xs mt-1" style={{ color: "var(--text-muted)" }}>
                          Registrado {timeAgo(p.created_at)}
                        </p>
                        {(p.photos || []).length > 0 && (
                          <p className="text-xs mt-1" style={{ color: "var(--text-muted)" }}>
                            {p.photos.length} foto{p.photos.length === 1 ? "" : "s"}
                          </p>
                        )}
                        {p.bio && (
                          <p className="text-xs mt-2 line-clamp-2" style={{ color: "var(--text-secondary)" }}>
                            {p.bio}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-2 border-t pt-3" style={{ borderColor: "var(--border-subtle)" }}>
                      <Link
                        to={`/musa/${p.slug || p.id}`}
                        target="_blank"
                        rel="noreferrer"
                        className="btn-ghost text-xs flex-1 text-center"
                        data-testid={`pending-preview-${p.id}`}
                      >
                        <ZoomIn size={12} className="inline mr-1" /> Ver perfil
                      </Link>
                      <button
                        className="btn-primary text-xs flex-1"
                        onClick={() => moderateProfile(p.id, "approve")}
                        data-testid={`pending-approve-${p.id}`}
                      >
                        <Check size={12} className="inline mr-1" /> Aprobar
                      </button>
                      <button
                        className="btn-ghost text-xs flex-1"
                        style={{ color: "#ef6d6d", borderColor: "#ef6d6d" }}
                        onClick={() => {
                          if (window.confirm(`¿Rechazar el perfil de ${p.stage_name}? No será visible al público.`)) {
                            moderateProfile(p.id, "reject");
                          }
                        }}
                        data-testid={`pending-reject-${p.id}`}
                      >
                        <X size={12} className="inline mr-1" /> Rechazar
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {tab === "profiles" && (
      <div className="overflow-x-auto" data-testid="admin-profiles-table">
        <table className="w-full text-sm" style={{ color: "var(--text-secondary)" }}>
          <thead>
            <tr style={{ borderBottom: "1px solid var(--border-accent)", color: "var(--brand-primary)" }}>
              <th className="text-left p-3">Nombre</th>
              <th className="text-left p-3">Ciudad</th>
              <th className="text-left p-3">Estado</th>
              <th className="text-left p-3">VIP</th>
              <th className="text-left p-3">Verificada</th>
              <th className="text-left p-3">Vistas</th>
              <th className="text-left p-3">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {profiles.map((p) => (
              <tr key={p.id} style={{ borderBottom: "1px solid var(--border-subtle)" }} data-testid={`admin-row-${p.id}`}>
                <td className="p-3" style={{ color: "var(--text-primary)" }}>
                  <Link to={`/musa/${p.slug || p.id}`} className="hover:underline">{p.stage_name}</Link>
                </td>
                <td className="p-3">{p.city}</td>
                <td className="p-3">
                  <span style={{
                    color: p.status === "approved" ? "#a8e6a3" : p.status === "banned" ? "#ef6d6d" : "var(--brand-primary)"
                  }}>{p.status}</span>
                </td>
                <td className="p-3">
                  <button
                    className="btn-ghost text-xs"
                    style={{ borderColor: p.is_vip ? "var(--vip-gold)" : "var(--border-subtle)", color: p.is_vip ? "var(--vip-gold)" : "var(--text-muted)" }}
                    onClick={async () => {
                      await api.patch(`/admin/profiles/${p.id}`, { is_vip: !p.is_vip });
                      load();
                    }}
                    data-testid={`toggle-vip-${p.id}`}
                  >
                    {p.is_vip ? "✓ VIP" : "Activar"}
                  </button>
                </td>
                <td className="p-3">
                  <button
                    className="btn-ghost text-xs"
                    style={{ borderColor: p.verified ? "var(--vip-gold)" : "var(--border-subtle)", color: p.verified ? "var(--vip-gold)" : "var(--text-muted)" }}
                    onClick={async () => {
                      await api.patch(`/admin/profiles/${p.id}`, { verified: !p.verified });
                      load();
                    }}
                    data-testid={`toggle-verified-${p.id}`}
                  >
                    {p.verified ? "✓ Verificada" : "Verificar"}
                  </button>
                </td>
                <td className="p-3">{p.views}</td>
                <td className="p-3">
                  <div className="flex gap-2 flex-wrap">
                    {(user?.role === "admin" || user?.role === "moderator") && (
                      <button className="btn-ghost text-xs" onClick={() => setEditingProfile(p)} data-testid={`edit-${p.id}`}>Editar</button>
                    )}
                    <button className="btn-ghost text-xs" onClick={() => setStatus(p.id, "approved")} data-testid={`approve-${p.id}`}>Aprobar</button>
                    <button className="btn-ghost text-xs" onClick={() => setStatus(p.id, "banned")} data-testid={`ban-${p.id}`}>Banear</button>
                    <button className="btn-ghost text-xs" onClick={() => setStatus(p.id, "pending")} data-testid={`pending-${p.id}`}>Pendiente</button>
                    <button
                      className="btn-ghost text-xs"
                      style={{ color: "#ef6d6d", borderColor: "#ef6d6d" }}
                      onClick={() => deleteProfile(p.id, p.stage_name)}
                      data-testid={`delete-${p.id}`}
                    >
                      Eliminar
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      )}

      {tab === "reviews" && (
        <div className="flex flex-col gap-3" data-testid="admin-reviews-list">
          {reviews.length === 0 ? <p style={{ color: "var(--text-secondary)" }}>Sin reseñas pendientes.</p> :
            reviews.map((r) => (
              <div key={r.id} className="p-5" style={{ background: "var(--bg-surface)", border: "1px solid var(--border-accent)" }} data-testid={`review-row-${r.id}`}>
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-serif text-lg" style={{ color: "var(--text-primary)" }}>{r.author_name}</span>
                      <span style={{ color: "var(--vip-gold)" }}>{"★".repeat(r.rating)}</span>
                    </div>
                    <p className="text-sm mt-2" style={{ color: "var(--text-secondary)" }}>{r.comment}</p>
                    <p className="text-xs mt-2" style={{ color: "var(--text-muted)" }}>Para perfil: {r.profile_id.slice(0,8)}…</p>
                  </div>
                  <div className="flex gap-2">
                    <button className="btn-ghost text-xs" onClick={() => moderateReview(r.id, "approve")} data-testid={`approve-review-${r.id}`}>Aprobar</button>
                    <button className="btn-ghost text-xs" style={{ color: "#ef6d6d" }} onClick={() => moderateReview(r.id, "reject")} data-testid={`reject-review-${r.id}`}>Rechazar</button>
                  </div>
                </div>
              </div>
            ))}
        </div>
      )}

      {tab === "photos" && (() => {
        const filteredPhotos = photos.filter((p) =>
          photoFilter === "all" ? true : (p.source || "dashboard") === photoFilter
        );
        const allVisibleSelected = filteredPhotos.length > 0 && filteredPhotos.every((p) => selectedPhotos.has(p.id));
        const toggleSelectAll = () => {
          setSelectedPhotos((prev) => {
            if (allVisibleSelected) {
              const n = new Set(prev);
              filteredPhotos.forEach((p) => n.delete(p.id));
              return n;
            }
            const n = new Set(prev);
            filteredPhotos.forEach((p) => n.add(p.id));
            return n;
          });
        };
        const counts = photos.reduce((acc, p) => {
          const src = p.source || "dashboard";
          acc[src] = (acc[src] || 0) + 1;
          return acc;
        }, {});
        return (
        <div data-testid="admin-photos-list">
          {/* Sticky toolbar: filters + bulk actions */}
          <div
            className="sticky top-0 z-10 flex flex-col md:flex-row md:items-center gap-3 md:gap-5 p-3 mb-4 flex-wrap"
            style={{ background: "var(--bg-base, #0A0A0A)", borderBottom: "1px solid var(--border-accent)" }}
            data-testid="photos-toolbar"
          >
            <div className="flex items-center gap-2 flex-wrap">
              {[
                { id: "all", label: `Todas (${photos.length})` },
                { id: "registration", label: `Registro (${counts.registration || 0})` },
                { id: "dashboard", label: `Panel Musa (${counts.dashboard || 0})` },
              ].map((f) => (
                <button
                  key={f.id}
                  onClick={() => setPhotoFilter(f.id)}
                  className="text-xs uppercase tracking-[0.15em] px-3 py-2 transition-all"
                  style={{
                    background: photoFilter === f.id ? "var(--bg-elevated)" : "transparent",
                    border: `1px solid ${photoFilter === f.id ? "var(--brand-primary)" : "var(--border-subtle)"}`,
                    color: photoFilter === f.id ? "var(--brand-primary)" : "var(--text-secondary)",
                  }}
                  data-testid={`photos-filter-${f.id}`}
                >
                  {f.label}
                </button>
              ))}
            </div>

            <div className="flex items-center gap-3 md:ml-auto flex-wrap">
              {filteredPhotos.length > 0 && (
                <label className="flex items-center gap-2 text-xs uppercase tracking-[0.15em]" style={{ color: "var(--text-secondary)" }}>
                  <input
                    type="checkbox"
                    checked={allVisibleSelected}
                    onChange={toggleSelectAll}
                    className="accent-[var(--brand-primary)]"
                    data-testid="photos-select-all"
                  />
                  Seleccionar todo
                </label>
              )}
              <span className="text-xs" style={{ color: "var(--text-muted)" }} data-testid="photos-selected-count">
                {selectedPhotos.size} seleccionada{selectedPhotos.size === 1 ? "" : "s"}
              </span>
              <button
                disabled={!selectedPhotos.size || bulkBusy}
                onClick={() => bulkModeratePhotos("approve")}
                className="btn-primary text-xs inline-flex items-center gap-2"
                style={{ opacity: !selectedPhotos.size || bulkBusy ? 0.4 : 1, cursor: !selectedPhotos.size ? "not-allowed" : "pointer" }}
                data-testid="photos-bulk-approve"
              >
                <Check size={14} strokeWidth={1.4} /> Aprobar {selectedPhotos.size > 0 ? `(${selectedPhotos.size})` : ""}
              </button>
              <button
                disabled={!selectedPhotos.size || bulkBusy}
                onClick={() => bulkModeratePhotos("reject")}
                className="btn-ghost text-xs inline-flex items-center gap-2"
                style={{ opacity: !selectedPhotos.size || bulkBusy ? 0.4 : 1, color: "#ef6d6d", borderColor: "#ef6d6d", cursor: !selectedPhotos.size ? "not-allowed" : "pointer" }}
                data-testid="photos-bulk-reject"
              >
                <X size={14} strokeWidth={1.4} /> Rechazar {selectedPhotos.size > 0 ? `(${selectedPhotos.size})` : ""}
              </button>
            </div>
          </div>

          {filteredPhotos.length === 0 ? (
            <div
              className="text-center py-20"
              style={{ background: "var(--bg-surface)", border: "1px dashed var(--border-subtle)" }}
              data-testid="photos-empty"
            >
              <ShieldCheck size={40} strokeWidth={1} color="var(--vip-gold)" className="mx-auto mb-4" />
              <p className="font-serif text-xl" style={{ color: "var(--text-primary)" }}>
                Cola limpia
              </p>
              <p className="text-xs mt-2" style={{ color: "var(--text-muted)" }}>
                No hay fotos pendientes de moderación.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredPhotos.map((p) => {
                const selected = selectedPhotos.has(p.id);
                const busy = busyPhotoIds.has(p.id);
                const source = p.source || "dashboard";
                return (
                  <div
                    key={p.id}
                    className="p-3 transition-all"
                    style={{
                      background: "var(--bg-surface)",
                      border: `1px solid ${selected ? "var(--brand-primary)" : "var(--border-accent)"}`,
                      opacity: busy ? 0.5 : 1,
                    }}
                    data-testid={`photo-row-${p.id}`}
                  >
                    <div className="relative mb-3" style={{ aspectRatio: "3/4", overflow: "hidden" }}>
                      <img
                        src={p.url}
                        alt=""
                        className="w-full h-full object-cover cursor-zoom-in"
                        onClick={() => setLightbox(p)}
                        data-testid={`photo-zoom-${p.id}`}
                      />
                      <label
                        className="absolute top-2 left-2 flex items-center justify-center cursor-pointer"
                        style={{
                          width: 28, height: 28, background: "rgba(10,10,10,0.75)",
                          border: `1px solid ${selected ? "var(--brand-primary)" : "var(--border-subtle)"}`,
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={selected}
                          onChange={() => togglePhoto(p.id)}
                          className="accent-[var(--brand-primary)]"
                          data-testid={`photo-select-${p.id}`}
                        />
                      </label>
                      <button
                        type="button"
                        onClick={() => setLightbox(p)}
                        className="absolute top-2 right-2 flex items-center gap-1 text-xs px-2 py-1"
                        style={{ background: "rgba(10,10,10,0.75)", color: "var(--text-primary)", border: "1px solid var(--border-subtle)" }}
                        data-testid={`photo-zoom-btn-${p.id}`}
                        aria-label="Ampliar"
                      >
                        <ZoomIn size={12} strokeWidth={1.4} /> Ver
                      </button>
                      <span
                        className="absolute bottom-2 left-2 text-[10px] uppercase tracking-[0.18em] px-2 py-1"
                        style={{
                          background: source === "registration" ? "rgba(212,175,55,0.18)" : "rgba(200,16,46,0.18)",
                          color: source === "registration" ? "var(--vip-gold)" : "var(--brand-primary)",
                          border: `1px solid ${source === "registration" ? "var(--vip-gold)" : "var(--brand-primary)"}`,
                        }}
                        data-testid={`photo-source-${p.id}`}
                      >
                        {source === "registration" ? "Registro" : "Panel"}
                      </span>
                    </div>

                    <div className="flex items-start gap-2 mb-1">
                      {p.profile_id ? (
                        <Link
                          to={`/musa/${p.profile_id}`}
                          className="text-sm font-serif hover:underline inline-flex items-center gap-1"
                          style={{ color: "var(--text-primary)" }}
                          data-testid={`photo-profile-link-${p.id}`}
                        >
                          <UserCircle2 size={14} strokeWidth={1.3} /> {p.stage_name}
                        </Link>
                      ) : (
                        <span className="text-sm font-serif inline-flex items-center gap-1" style={{ color: "var(--text-primary)" }}>
                          <ImageIcon size={14} strokeWidth={1.3} /> {p.stage_name || "—"}
                        </span>
                      )}
                    </div>
                    <div className="text-xs mb-3 flex items-center justify-between" style={{ color: "var(--text-muted)" }}>
                      <span className="truncate" data-testid={`photo-email-${p.id}`}>{p.user_email}</span>
                      <span className="ml-2 shrink-0">{timeAgo(p.created_at)}</span>
                    </div>

                    <div className="flex gap-2">
                      <button
                        className="btn-primary text-xs flex-1 inline-flex items-center justify-center gap-1"
                        disabled={busy}
                        onClick={() => moderatePhoto(p.id, "approve")}
                        data-testid={`approve-photo-${p.id}`}
                      >
                        <Check size={12} strokeWidth={1.5} /> Aprobar
                      </button>
                      <button
                        className="btn-secondary text-xs flex-1 inline-flex items-center justify-center gap-1"
                        disabled={busy}
                        style={{ color: "#ef6d6d", borderColor: "#ef6d6d" }}
                        onClick={() => moderatePhoto(p.id, "reject")}
                        data-testid={`reject-photo-${p.id}`}
                      >
                        <X size={12} strokeWidth={1.5} /> Rechazar
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {lightbox && (
            <div
              className="fixed inset-0 z-[150] flex items-center justify-center p-6 cursor-zoom-out"
              style={{ background: "rgba(0,0,0,0.92)", backdropFilter: "blur(6px)" }}
              onClick={() => setLightbox(null)}
              data-testid="photo-lightbox"
            >
              <button
                className="absolute top-5 right-5 text-white p-2"
                style={{ background: "rgba(0,0,0,0.5)", border: "1px solid rgba(255,255,255,0.2)" }}
                onClick={() => setLightbox(null)}
                data-testid="photo-lightbox-close"
                aria-label="Cerrar"
              >
                <X size={22} strokeWidth={1.2} />
              </button>
              <img
                src={lightbox.url}
                alt=""
                className="max-w-full max-h-full object-contain"
                onClick={(e) => e.stopPropagation()}
              />
            </div>
          )}
        </div>
        );
      })()}

      {tab === "verifs" && (
        <div className="flex flex-col gap-3" data-testid="admin-verifs-list">
          {verifs.length === 0 ? <p style={{ color: "var(--text-secondary)" }}>Sin verificaciones pendientes.</p> :
            verifs.map((v) => (
              <div key={v.id} className="p-5" style={{ background: "var(--bg-surface)", border: "1px solid var(--border-accent)" }} data-testid={`verif-row-${v.id}`}>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
                  <div>
                    <div className="overline mb-1">INE · Frente</div>
                    {v.id_front_url || v.id_photo_url ? (
                      <a href={v.id_front_url || v.id_photo_url} target="_blank" rel="noreferrer">
                        <img src={v.id_front_url || v.id_photo_url} alt="" className="w-full max-h-36 object-cover" />
                      </a>
                    ) : <div className="text-xs" style={{ color: "var(--text-muted)" }}>—</div>}
                  </div>
                  <div>
                    <div className="overline mb-1">INE · Reverso</div>
                    {v.id_back_url ? (
                      <a href={v.id_back_url} target="_blank" rel="noreferrer">
                        <img src={v.id_back_url} alt="" className="w-full max-h-36 object-cover" />
                      </a>
                    ) : <div className="text-xs" style={{ color: "var(--text-muted)" }}>(legacy)</div>}
                  </div>
                  <div>
                    <div className="overline mb-1">Selfie</div>
                    {v.selfie_url ? (
                      <a href={v.selfie_url} target="_blank" rel="noreferrer">
                        <img src={v.selfie_url} alt="" className="w-full max-h-36 object-cover" />
                      </a>
                    ) : <div className="text-xs" style={{ color: "var(--text-muted)" }}>—</div>}
                  </div>
                  <div>
                    <div className="overline mb-1">Selfie con INE</div>
                    {v.selfie_with_id_url ? (
                      <a href={v.selfie_with_id_url} target="_blank" rel="noreferrer">
                        <img src={v.selfie_with_id_url} alt="" className="w-full max-h-36 object-cover" />
                      </a>
                    ) : <div className="text-xs" style={{ color: "var(--text-muted)" }}>(legacy)</div>}
                  </div>
                  <div>
                    <div className="overline mb-1">Video (en vivo)</div>
                    {v.video_url ? (
                      <video src={v.video_url} controls className="w-full max-h-36" />
                    ) : <div className="text-xs" style={{ color: "var(--text-muted)" }}>(legacy)</div>}
                  </div>
                </div>
                {v.notes && <p className="text-xs mb-2" style={{ color: "var(--text-secondary)" }}>Nota: {v.notes}</p>}
                {v.voluntary_consent ? (
                  <p className="text-xs mb-3 inline-flex items-center gap-2 px-2 py-1" style={{ color: "var(--vip-gold)", background: "rgba(212,175,55,0.08)", border: "1px solid var(--vip-gold)" }} data-testid={`verif-consent-${v.id}`}>
                    <ShieldCheck size={12} /> Consentimiento informado firmado {v.consent_recorded_at ? `· ${new Date(v.consent_recorded_at).toLocaleString("es-MX")}` : ""}
                  </p>
                ) : (
                  <p className="text-xs mb-3" style={{ color: "#ef6d6d" }} data-testid={`verif-no-consent-${v.id}`}>
                    ⚠ Sin consentimiento explícito (envío anterior al sistema actual).
                  </p>
                )}
                <div className="flex gap-2">
                  <button className="btn-primary text-xs" onClick={() => moderateVerif(v.id, "approve")} data-testid={`approve-verif-${v.id}`}>Aprobar</button>
                  <button className="btn-ghost text-xs" style={{ color: "#ef6d6d" }} onClick={() => moderateVerif(v.id, "reject")} data-testid={`reject-verif-${v.id}`}>Rechazar</button>
                </div>
              </div>
            ))}
        </div>
      )}

      {tab === "reports" && (
        <div className="flex flex-col gap-3" data-testid="admin-reports-list">
          {reports.length === 0 ? <p style={{ color: "var(--text-secondary)" }}>Sin reportes activos. 🎉</p> :
            reports.map((r) => (
              <div key={r.id} className="p-5" style={{ background: "var(--bg-surface)", border: "1px solid #ef6d6d" }} data-testid={`report-row-${r.id}`}>
                <div className="flex justify-between items-start gap-4 flex-wrap">
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <span className="overline" style={{ color: "#ef6d6d" }}>Motivo: {r.reason}</span>
                      <span className="text-xs" style={{ color: "var(--text-muted)" }}>{new Date(r.created_at).toLocaleDateString("es-MX")}</span>
                    </div>
                    <p className="font-serif text-lg" style={{ color: "var(--text-primary)" }}>Perfil: {r.profile_name}</p>
                    {r.details && <p className="text-sm mt-2" style={{ color: "var(--text-secondary)" }}>"{r.details}"</p>}
                    <p className="text-xs mt-2" style={{ color: "var(--text-muted)" }}>Reportado por: {r.user_email} · IP {r.ip}</p>
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    <button className="btn-ghost text-xs" onClick={() => resolveReport(r.id, "dismiss")} data-testid={`dismiss-report-${r.id}`}>Descartar</button>
                    <button className="btn-secondary text-xs" style={{ color: "#D4AF37", borderColor: "#D4AF37" }} onClick={() => resolveReport(r.id, "warn")} data-testid={`warn-report-${r.id}`}>Advertir</button>
                    <button className="btn-primary text-xs" style={{ background: "#ef6d6d", borderColor: "#ef6d6d" }} onClick={() => resolveReport(r.id, "suspend")} data-testid={`suspend-report-${r.id}`}>Suspender perfil</button>
                  </div>
                </div>
              </div>
            ))}
        </div>
      )}

      {showCreate && (
        <CreateProfileModal
          onClose={() => setShowCreate(false)}
          onCreated={() => { setShowCreate(false); load(); }}
        />
      )}

      {editingProfile && (
        <EditProfileModal
          profile={editingProfile}
          onClose={() => setEditingProfile(null)}
          onSaved={() => { setEditingProfile(null); load(); }}
        />
      )}

      {user?.email?.toLowerCase() === BOSS_EMAIL && <BossYouTubePlayer />}
    </div>
  );
}

function CreateProfileModal({ onClose, onCreated }) {
  const [form, setForm] = useState({
    stage_name: "", age: 22, city: "CDMX", state: "Ciudad de México",
    gender: "mujer", phone: "", whatsapp: "",
    bio: "",
    services: "1 hora, 2 horas, Salida hotel",
    languages: "Español",
    categories: [],
    height_cm: "",
    hero_photo: "",
    photos: "",
    rate_1h: "", rate_2h: "", rate_overnight: "", rate_outcall: "",
    rate_currency: "MXN", rate_notes: "",
    verified: true,
    is_vip: true,
    status: "approved",
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const addCategory = (cat) => {
    setForm((f) => f.categories.includes(cat)
      ? { ...f, categories: f.categories.filter((c) => c !== cat) }
      : { ...f, categories: [...f.categories, cat] });
  };

  const submit = async () => {
    if (!form.stage_name || !form.city) {
      setError("Nombre artístico y ciudad son obligatorios.");
      return;
    }
    setSaving(true); setError("");
    try {
      const photosList = form.photos.split("\n").map((s) => s.trim()).filter(Boolean);
      const servicesList = form.services.split(",").map((s) => s.trim()).filter(Boolean);
      const languagesList = form.languages.split(",").map((s) => s.trim()).filter(Boolean);
      const payload = {
        ...form,
        age: parseInt(form.age) || 22,
        height_cm: form.height_cm ? parseInt(form.height_cm) : null,
        photos: photosList,
        hero_photo: form.hero_photo || (photosList[0] || null),
        services: servicesList,
        languages: languagesList,
      };
      // Tariffs are owned by the Musa from her own panel — admin doesn't push them.
      delete payload.rate_30min;
      delete payload.rate_1h;
      delete payload.rate_2h;
      delete payload.rate_overnight;
      delete payload.rate_outcall;
      delete payload.rate_currency;
      delete payload.rate_notes;
      const { data } = await api.post("/admin/profiles/create", payload);
      alert(`✓ Perfil + cuenta creados\n\nEmail: ${data.email}\nContraseña: ${data.default_password}\n\nGuarda estos datos para entregárselos a la Musa.`);
      onCreated();
    } catch (e) {
      setError(e.response?.data?.detail?.[0]?.msg || e.response?.data?.detail || e.message);
    } finally { setSaving(false); }
  };

  const CATS = ["milf", "teen", "curvy", "petite", "modelo", "universitaria", "latina", "trans", "pareja"];

  return (
    <div className="fixed inset-0 z-[300] flex items-center justify-center p-4 overflow-y-auto" style={{ background: "rgba(5,5,5,0.92)", backdropFilter: "blur(8px)" }} data-testid="create-profile-modal">
      <div className="w-full max-w-2xl my-8 p-6" style={{ background: "var(--bg-surface)", border: "1px solid var(--brand-primary)" }}>
        <div className="flex items-center justify-between mb-5">
          <h2 className="font-serif text-2xl" style={{ color: "var(--text-primary)" }}>Crear perfil manualmente</h2>
          <button onClick={onClose} className="btn-ghost text-xs" data-testid="create-profile-close"><X size={14} /></button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <input className="input-field md:col-span-2" placeholder="Nombre artístico *" value={form.stage_name} onChange={(e)=>setForm({...form, stage_name:e.target.value})} data-testid="cp-stage-name" />
          <input type="number" className="input-field" placeholder="Edad" value={form.age} onChange={(e)=>setForm({...form, age:e.target.value})} data-testid="cp-age" />
          <select className="input-field" value={form.gender} onChange={(e)=>setForm({...form, gender:e.target.value})} data-testid="cp-gender">
            <option value="mujer">Mujer</option>
            <option value="hombre">Hombre</option>
            <option value="trans">Trans</option>
          </select>
          <input className="input-field" placeholder="Ciudad *" value={form.city} onChange={(e)=>setForm({...form, city:e.target.value})} data-testid="cp-city" />
          <input className="input-field" placeholder="Estado" value={form.state} onChange={(e)=>setForm({...form, state:e.target.value})} data-testid="cp-state" />
          <input className="input-field" placeholder="WhatsApp (+52...)" value={form.whatsapp} onChange={(e)=>setForm({...form, whatsapp:e.target.value, phone:e.target.value})} data-testid="cp-whatsapp" />
          <input type="number" className="input-field" placeholder="Estatura (cm)" value={form.height_cm} onChange={(e)=>setForm({...form, height_cm:e.target.value})} data-testid="cp-height" />
          <textarea className="input-field md:col-span-2 h-20" placeholder="Bio / descripción" value={form.bio} onChange={(e)=>setForm({...form, bio:e.target.value})} data-testid="cp-bio" />
          <input className="input-field md:col-span-2" placeholder="Foto principal (URL)" value={form.hero_photo} onChange={(e)=>setForm({...form, hero_photo:e.target.value})} data-testid="cp-hero" />
          <textarea className="input-field md:col-span-2 h-20" placeholder="URLs de fotos adicionales (una por línea)" value={form.photos} onChange={(e)=>setForm({...form, photos:e.target.value})} data-testid="cp-photos" />
          <input className="input-field md:col-span-2" placeholder="Servicios (separados por coma)" value={form.services} onChange={(e)=>setForm({...form, services:e.target.value})} data-testid="cp-services" />
          <input className="input-field md:col-span-2" placeholder="Idiomas (separados por coma)" value={form.languages} onChange={(e)=>setForm({...form, languages:e.target.value})} data-testid="cp-languages" />

          <div className="md:col-span-2">
            <div className="overline mb-2">Categorías</div>
            <div className="flex flex-wrap gap-2">
              {CATS.map((c) => (
                <button key={c} type="button" onClick={()=>addCategory(c)} className="text-xs px-3 py-1"
                  style={{ background: form.categories.includes(c) ? "var(--brand-primary)" : "var(--bg-elevated)", color: form.categories.includes(c) ? "#fff" : "var(--text-secondary)", border: "1px solid var(--border-subtle)" }}
                  data-testid={`cp-cat-${c}`}>{c}</button>
              ))}
            </div>
          </div>

          <div className="md:col-span-2 pt-3" style={{ borderTop: "1px solid var(--border-subtle)" }}>
            <p className="text-xs italic" style={{ color: "var(--text-muted)" }} data-testid="cp-tariffs-note">
              Las tarifas las fija cada Musa desde su propio panel. Admin no las edita aquí.
            </p>
          </div>

          <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-3 gap-3 pt-3" style={{ borderTop: "1px solid var(--border-subtle)" }}>
            <label className="flex items-center gap-2 p-2 cursor-pointer" style={{ background: form.verified ? "rgba(212,175,55,0.1)" : "var(--bg-elevated)", border: `1px solid ${form.verified ? "var(--vip-gold)" : "var(--border-subtle)"}` }}>
              <input type="checkbox" checked={form.verified} onChange={(e)=>setForm({...form, verified:e.target.checked})} data-testid="cp-verified" />
              <span className="text-sm" style={{ color: form.verified ? "var(--vip-gold)" : "var(--text-secondary)" }}>✓ Verificada (sello dorado)</span>
            </label>
            <label className="flex items-center gap-2 p-2 cursor-pointer" style={{ background: form.is_vip ? "rgba(212,175,55,0.1)" : "var(--bg-elevated)", border: `1px solid ${form.is_vip ? "var(--vip-gold)" : "var(--border-subtle)"}` }}>
              <input type="checkbox" checked={form.is_vip} onChange={(e)=>setForm({...form, is_vip:e.target.checked})} data-testid="cp-vip" />
              <span className="text-sm" style={{ color: form.is_vip ? "var(--vip-gold)" : "var(--text-secondary)" }}>👑 VIP (7 días)</span>
            </label>
            <select className="input-field" value={form.status} onChange={(e)=>setForm({...form, status:e.target.value})} data-testid="cp-status">
              <option value="approved">Aprobado (visible)</option>
              <option value="pending">Pendiente</option>
              <option value="banned">Baneado</option>
            </select>
          </div>
        </div>

        {error && <p className="text-xs mt-3" style={{ color: "#ef6d6d" }} data-testid="cp-error">{String(error)}</p>}

        <div className="flex gap-3 mt-6">
          <button onClick={onClose} className="btn-ghost flex-1" data-testid="cp-cancel">Cancelar</button>
          <button onClick={submit} disabled={saving} className="btn-primary flex-1" data-testid="cp-submit">
            {saving ? "Creando…" : "Crear perfil"}
          </button>
        </div>
      </div>
    </div>
  );
}


function EditProfileModal({ profile, onClose, onSaved }) {
  const [form, setForm] = useState({
    stage_name: profile.stage_name || "",
    age: profile.age || 22,
    city: profile.city || "",
    state: profile.state || "",
    bio: profile.bio || "",
    whatsapp: profile.whatsapp || "",
    phone: profile.phone || "",
    height_cm: profile.height_cm || "",
    services: (profile.services || []).join(", "),
    languages: (profile.languages || []).join(", "),
    categories: profile.categories || [],
    hero_photo: profile.hero_photo || "",
    photos: (profile.photos || []).join("\n"),
    rate_1h: profile.rate_1h || "",
    rate_2h: profile.rate_2h || "",
    rate_overnight: profile.rate_overnight || "",
    rate_outcall: profile.rate_outcall || "",
    rate_currency: profile.rate_currency || "MXN",
    rate_notes: profile.rate_notes || "",
    verified: !!profile.verified,
    is_vip: !!profile.is_vip,
    status: profile.status || "approved",
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const addCategory = (cat) => {
    setForm((f) => f.categories.includes(cat)
      ? { ...f, categories: f.categories.filter((c) => c !== cat) }
      : { ...f, categories: [...f.categories, cat] });
  };

  const submit = async () => {
    setSaving(true); setError("");
    try {
      const photosList = form.photos.split("\n").map((s) => s.trim()).filter(Boolean);
      await api.patch(`/admin/profiles/${profile.id}`, {
        stage_name: form.stage_name,
        age: parseInt(form.age) || profile.age,
        city: form.city,
        state: form.state,
        bio: form.bio,
        whatsapp: form.whatsapp,
        phone: form.phone || form.whatsapp,
        height_cm: form.height_cm ? parseInt(form.height_cm) : null,
        services: form.services.split(",").map((s) => s.trim()).filter(Boolean),
        languages: form.languages.split(",").map((s) => s.trim()).filter(Boolean),
        categories: form.categories,
        hero_photo: form.hero_photo || (photosList[0] || null),
        photos: photosList,
        // Tariffs are owned by the Musa — admin no longer overwrites them.
        verified: form.verified,
        is_vip: form.is_vip,
        status: form.status,
      });
      onSaved();
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally { setSaving(false); }
  };

  const CATS = ["milf", "teen", "curvy", "petite", "modelo", "universitaria", "latina", "trans", "pareja"];

  return (
    <div className="fixed inset-0 z-[300] flex items-center justify-center p-4 overflow-y-auto" style={{ background: "rgba(5,5,5,0.92)", backdropFilter: "blur(8px)" }} data-testid="edit-profile-modal">
      <div className="w-full max-w-2xl my-8 p-6" style={{ background: "var(--bg-surface)", border: "1px solid var(--brand-primary)" }}>
        <div className="flex items-center justify-between mb-5">
          <h2 className="font-serif text-2xl" style={{ color: "var(--text-primary)" }}>Editar · {profile.stage_name}</h2>
          <button onClick={onClose} className="btn-ghost text-xs" data-testid="edit-profile-close"><X size={14} /></button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <input className="input-field md:col-span-2" placeholder="Nombre artístico" value={form.stage_name} onChange={(e)=>setForm({...form, stage_name:e.target.value})} data-testid="ep-stage-name" />
          <input type="number" className="input-field" placeholder="Edad" value={form.age} onChange={(e)=>setForm({...form, age:e.target.value})} />
          <input type="number" className="input-field" placeholder="Estatura (cm)" value={form.height_cm} onChange={(e)=>setForm({...form, height_cm:e.target.value})} />
          <input className="input-field" placeholder="Ciudad" value={form.city} onChange={(e)=>setForm({...form, city:e.target.value})} />
          <input className="input-field" placeholder="Estado" value={form.state} onChange={(e)=>setForm({...form, state:e.target.value})} />
          <input className="input-field md:col-span-2" placeholder="WhatsApp" value={form.whatsapp} onChange={(e)=>setForm({...form, whatsapp:e.target.value})} />
          <textarea className="input-field md:col-span-2 h-20" placeholder="Bio" value={form.bio} onChange={(e)=>setForm({...form, bio:e.target.value})} />
          <input className="input-field md:col-span-2" placeholder="Foto principal (URL)" value={form.hero_photo} onChange={(e)=>setForm({...form, hero_photo:e.target.value})} />
          <textarea className="input-field md:col-span-2 h-24" placeholder="URLs de fotos (una por línea)" value={form.photos} onChange={(e)=>setForm({...form, photos:e.target.value})} />
          <input className="input-field md:col-span-2" placeholder="Servicios (separados por coma)" value={form.services} onChange={(e)=>setForm({...form, services:e.target.value})} />
          <input className="input-field md:col-span-2" placeholder="Idiomas (separados por coma)" value={form.languages} onChange={(e)=>setForm({...form, languages:e.target.value})} />

          <div className="md:col-span-2">
            <div className="overline mb-2">Categorías</div>
            <div className="flex flex-wrap gap-2">
              {CATS.map((c) => (
                <button key={c} type="button" onClick={()=>addCategory(c)} className="text-xs px-3 py-1"
                  style={{ background: form.categories.includes(c) ? "var(--brand-primary)" : "var(--bg-elevated)", color: form.categories.includes(c) ? "#fff" : "var(--text-secondary)", border: "1px solid var(--border-subtle)" }}
                  data-testid={`ep-cat-${c}`}>{c}</button>
              ))}
            </div>
          </div>

          <div className="md:col-span-2 pt-3" style={{ borderTop: "1px solid var(--border-subtle)" }}>
            <p className="text-xs italic" style={{ color: "var(--text-muted)" }} data-testid="ep-tariffs-note">
              Las tarifas las fija cada Musa desde su propio panel. Admin no las edita aquí.
            </p>
          </div>

          <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-3 gap-3 pt-3" style={{ borderTop: "1px solid var(--border-subtle)" }}>
            <label className="flex items-center gap-2 p-2 cursor-pointer" style={{ background: form.verified ? "rgba(212,175,55,0.1)" : "var(--bg-elevated)", border: `1px solid ${form.verified ? "var(--vip-gold)" : "var(--border-subtle)"}` }}>
              <input type="checkbox" checked={form.verified} onChange={(e)=>setForm({...form, verified:e.target.checked})} data-testid="ep-verified" />
              <span className="text-sm" style={{ color: form.verified ? "var(--vip-gold)" : "var(--text-secondary)" }}>✓ Verificada</span>
            </label>
            <label className="flex items-center gap-2 p-2 cursor-pointer" style={{ background: form.is_vip ? "rgba(212,175,55,0.1)" : "var(--bg-elevated)", border: `1px solid ${form.is_vip ? "var(--vip-gold)" : "var(--border-subtle)"}` }}>
              <input type="checkbox" checked={form.is_vip} onChange={(e)=>setForm({...form, is_vip:e.target.checked})} data-testid="ep-vip" />
              <span className="text-sm" style={{ color: form.is_vip ? "var(--vip-gold)" : "var(--text-secondary)" }}>👑 VIP</span>
            </label>
            <select className="input-field" value={form.status} onChange={(e)=>setForm({...form, status:e.target.value})}>
              <option value="approved">Aprobado</option>
              <option value="pending">Pendiente</option>
              <option value="banned">Baneado</option>
            </select>
          </div>
        </div>

        {error && <p className="text-xs mt-3" style={{ color: "#ef6d6d" }}>{String(error)}</p>}

        <div className="flex gap-3 mt-6">
          <button onClick={onClose} className="btn-ghost flex-1">Cancelar</button>
          <button onClick={submit} disabled={saving} className="btn-primary flex-1" data-testid="ep-submit">
            {saving ? "Guardando…" : "Guardar cambios"}
          </button>
        </div>
      </div>
    </div>
  );
}
